import { useEffect } from "react";
import { ArrowLeft, Download } from "lucide-react";
import { LogoMark, BrandName } from "@/components/landing/LogoMark";

const ASSETS = [
    {
        id: "lockup-dark",
        title: "Volledig logo — donker",
        desc: "Embleem + naam + ondertitel, goud op donker. Voor de website, presentaties en donkere drukwerk.",
        svg: "/logo/cbs-lockup-dark.svg",
        png: "/logo/cbs-lockup-dark.png",
        bg: "bg-black",
    },
    {
        id: "lockup-light",
        title: "Volledig logo — licht",
        desc: "Gouden embleem met inktzwarte tekst op licht. Voor briefpapier, facturen en lichte drukwerk.",
        svg: "/logo/cbs-lockup-light.svg",
        png: "/logo/cbs-lockup-light.png",
        bg: "bg-[#F4EFE6]",
    },
    {
        id: "emblem",
        title: "Alleen het embleem",
        desc: "Het CBS-merkteken zonder naam. Voor favicons, social-profielen en als watermerk.",
        svg: "/logo/cbs-emblem.svg",
        png: "/logo/cbs-emblem.png",
        bg: "bg-black",
    },
    {
        id: "briefpapier",
        title: "Briefpapier",
        desc: "Drukklaar A4-briefpapier (wit) met de lichte logo-versie, je contactgegevens en KvK/SBI in de voettekst. Voor brieven en offertes.",
        svg: "/briefpapier/clarke-briefpapier.pdf",
        png: null,
        bg: "bg-white",
        img: "/logo/cbs-lockup-light.svg",
    },
];

export default function LogoConceptsPage() {
    useEffect(() => {
        document.title = "Logo — Clarke Business Solutions";
    }, []);

    return (
        <div data-testid="logo-page" className="min-h-screen bg-ink text-bone">
            <div className="noise-overlay" aria-hidden="true" />
            <header className="border-b border-white/10">
                <div className="mx-auto flex max-w-[1100px] items-center justify-between px-6 py-5">
                    <div className="flex items-center gap-3">
                        <LogoMark className="h-8 w-8" />
                        <BrandName />
                    </div>
                    <a data-testid="logo-back-link" href="/" className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-ash transition-colors hover:text-gold">
                        <ArrowLeft className="h-3.5 w-3.5" /> Terug naar site
                    </a>
                </div>
            </header>

            <main className="mx-auto max-w-[1100px] px-6 py-16 md:py-24">
                <p className="text-[11px] uppercase tracking-[0.35em] text-gold">Huisstijl</p>
                <h1 className="mt-4 font-display text-4xl tracking-tight md:text-5xl">Het Clarke-embleem</h1>
                <p className="mt-4 max-w-2xl text-sm leading-relaxed text-ash">
                    Het definitieve logo van Clarke Business Solutions. De SVG-bestanden zijn vectoren met het
                    lettertype ingebed — oneindig schaalbaar en direct geschikt voor de drukker (visitekaartjes,
                    briefpapier, werkkleding). De PNG's zijn hoge-resolutie previews.
                </p>

                <div className="mt-16 space-y-10">
                    {ASSETS.map((a) => (
                        <section key={a.id} data-testid={`logo-asset-${a.id}`} className="border border-white/10 bg-coal p-8 md:p-12">
                            <div className="grid grid-cols-1 items-center gap-10 md:grid-cols-12">
                                <div className={`flex items-center justify-center border border-white/10 ${a.bg} p-10 md:col-span-5`}>
                                    <img src={a.img || a.svg} alt={a.title} className={a.id === "emblem" ? "h-44 w-44" : "w-64"} />
                                </div>
                                <div className="md:col-span-7">
                                    <h2 className="font-display text-2xl tracking-tight text-bone">{a.title}</h2>
                                    <p className="mt-3 max-w-md text-sm leading-relaxed text-ash">{a.desc}</p>
                                    <div className="mt-8 flex flex-wrap gap-4">
                                        <a
                                            data-testid={`download-${a.id}-svg`}
                                            href={a.svg}
                                            download
                                            className="flex items-center gap-2 bg-gold px-6 py-3 text-[11px] uppercase tracking-[0.2em] text-black transition-colors hover:bg-bone"
                                        >
                                            <Download className="h-3.5 w-3.5" /> {a.svg.endsWith(".pdf") ? "PDF (printen)" : "SVG (drukker)"}
                                        </a>
                                        {a.png ? (
                                            <a
                                                data-testid={`download-${a.id}-png`}
                                                href={a.png}
                                                download
                                                className="flex items-center gap-2 border border-white/15 px-6 py-3 text-[11px] uppercase tracking-[0.2em] text-ash transition-colors hover:border-gold/60 hover:text-gold"
                                            >
                                                <Download className="h-3.5 w-3.5" /> PNG (hoge resolutie)
                                            </a>
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                        </section>
                    ))}
                </div>
            </main>
        </div>
    );
}
